import { Avatar, AvatarGroup, Box, Button, ButtonGroup, Collapse, Divider, Popover, Tooltip, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import api from '../../../service/api'
import DataLoadingSpinner from '../../../components/common/DataLoadingSpinner'
import TryAgainMessageBlock from '../../../components/common/TryAgainMessageBlock'
import CustomChip from '../../../components/common/CustomChip'
import { debounce, formatDate, Icons } from '../../../utils/utilities'
import dayjs from 'dayjs'
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import CustomSearchBar from '../../../components/common/CustomSearchBar'
import noLogsImg from '../../../assets/no-logs.svg'

const oneDayMilliseconds = 60 * 60 * 24 * 1000

const getNameInitials = ( name ) => {
    try {
        const nameParts = name?.split( " " )
        return nameParts.map( n => n?.at( 0 ) ).slice( 0, 2 )
    } catch ( err ) {
        return name?.at( 0 )
    }
}

const getColor = ( method ) => {
    switch ( method ) {
        case "GET":
            return "success"

        case "POST":
            return "primary"

        case "DELETE":
            return "error"

        case "PUT":
        case "PATCH":
            return "secondary"

        default:
            return "greyed"
    }
}

const LogBox = ( { log, handlePopoverOpen, handlePopoverClose } ) => {

    const [openDetails, setOpenDetails] = useState( false )

    const { layout, palette, border } = useTheme()

    const method = log.method || log.action
    const action = log.action !== method ? log.action : ""
    const color = getColor( method )

    return (
        <Box maxWidth="900px" padding="10px" borderRadius={layout.borderRadius} border={border[1]}>

            <Box display="flex" flexDirection={{ md: "row", xs: "column" }} gap={layout.cardsGap} width="100%"  >
                <Box display={{ md: "flex", xs: "none" }} flexDirection="column" alignItems="center" justifyContent="flex-start" width="120px">
                    <Typography fontSize="20px" color='textSecondary'>{Icons.default.Calendar}</Typography>
                    <Typography fontSize="16px" fontWeight={600}>{dayjs( log.createdAt ).format( "ll" )}</Typography>
                    <Typography fontSize="12px">{dayjs( log.createdAt ).format( "hh:mm A" )}</Typography>
                </Box>
                <Box display={{ md: "none", xs: "flex" }}>
                    <CustomChip variant='filled' startIcon={Icons.default.Calendar} value={dayjs( log.createdAt ).format( "LLL" )} />
                </Box>
                <Box flexGrow={1}>
                    <Box display="flex" gap="5px" alignItems="center" flexWrap="wrap" >
                        <CustomChip color={color} value={method} />
                        <CustomChip value={action} />
                        <CustomChip tooltipTitle='Alive Module' value={log.resource || log.entity} />
                        <CustomChip textStyles={{ textTransform: "capitalize" }} tooltipTitle='Environment' value={log.action_env} />
                    </Box>
                    <Box display="flex" flexWrap="wrap" gap={layout.cardsGap} alignItems="flex-end" justifyContent="space-between" >
                        <Box marginTop="5px" display="flex" gap={layout.cardsGap} alignItems="center" >
                            <AvatarGroup spacing={16}>
                                {log.action_env === "impersonation" && <Avatar sx={{ background: palette.primary.main, color: palette.primary.contrastText, width: "30px", height: "30px", textTransform: "uppercase", fontSize: "16px" }} >{getNameInitials( log.impersonated_user_details?.name )}</Avatar>}
                                <Avatar sx={{ background: palette.primary.main, color: palette.primary.contrastText, width: "30px", height: "30px", textTransform: "uppercase", fontSize: "16px" }} >{getNameInitials( log.auditor_details?.user_name )}</Avatar>
                            </AvatarGroup>
                            <Box>
                                {log.action_env !== "impersonation" && <Typography variant='subtitle2' >{log.auditor_details.user_name}</Typography>}
                                {log.action_env === "impersonation" && <Typography>
                                    <Typography variant='subtitle2' fontWeight="normal" >{log.auditor_details.user_name} </Typography>
                                    <Typography variant='caption' component="em">impersonating as </Typography>
                                    <Typography
                                        onMouseEnter={event => handlePopoverOpen( event, log.impersonated_user_details )}
                                        onMouseLeave={handlePopoverClose}
                                        variant='subtitle2'
                                        fontSize="12px"
                                        fontWeight="normal"
                                        position="relative"
                                        sx={{
                                            cursor: "pointer",
                                            "&:after": {
                                                content: "''",
                                                position: "absolute",
                                                display: "block",
                                                bottom: 0,
                                                left: 0,
                                                right: 0,
                                                width: "100%",
                                                borderBottom: `1px dashed ${palette.text.secondary}`,
                                                zIndex: 10
                                            }
                                        }}
                                    >
                                        {log.impersonated_user_details.name}
                                    </Typography>
                                </Typography>}
                                <Typography fontSize="12px" >{log.auditor_details.user_email}</Typography>
                            </Box>
                        </Box>
                        <Button onClick={() => setOpenDetails( prev => !prev )} color='greyed' variant='outlined' sx={{ borderRadius: "20px" }} size='small' endIcon={openDetails ? Icons.default.KeyboardArrowUp : Icons.default.KeyboardArrowDownIcon} >
                            {openDetails ? "Close Details" : "Open Details"}
                        </Button>
                    </Box>
                </Box>
            </Box>
            <Collapse in={openDetails}  >
                <Box display="flex" flexDirection="column" gap={layout.cardsGap} marginTop="10px" padding="10px 0" borderTop={border[1]}>
                    <Box>
                        <Typography variant='subtitle2' component="h6">Endpoint</Typography>
                        <Typography fontSize="12px" component="span">{log.request_details.endpoint}</Typography>
                    </Box>
                    <Box>
                        <Typography variant='subtitle2' component="h6">Request Details</Typography>
                        <Box >
                            <Typography maxHeight="200px" overflow="auto" className='slim-custom-scrollbar' fontSize="12px" padding="10px" borderRadius={layout.borderRadius} bgcolor={palette.contentBg} component="pre">{JSON.stringify( log.request_details, null, 3 )}</Typography>
                        </Box>
                    </Box>
                    {log.resource_details && <Box>
                        <Typography variant='subtitle2' component="h6">Resource Details</Typography>
                        <Box >
                            <Typography maxHeight="200px" overflow="auto" className='slim-custom-scrollbar' fontSize="12px" padding="10px" borderRadius={layout.borderRadius} bgcolor={palette.contentBg} component="pre">{JSON.stringify( log.resource_details, null, 3 )}</Typography>
                        </Box>
                    </Box>}
                </Box>
            </Collapse>
        </Box>
    )
}

const AdminActivityLogs = () => {

    const [userInfoPopoveAnchorEl, setUserInfoPopoveAnchorEl] = useState( null )
    const [datePickerOpenState, setDatePickerOpenState] = useState( false )
    const [hoveredUserData, setHoveredUserData] = useState( null )

    const [logsPerPage, setLogsPerPage] = useState( 300 )
    const [filterDate, setFilterDate] = useState( null )
    const [searchText, setSearchText] = useState( '' )
    const [filterText, setFilterText] = useState( '' )
    const [loading, setLoading] = useState( true )
    const [error, setError] = useState( null )
    const [logs, setLogs] = useState( [] )
    const [page, setPage] = useState( 1 )

    const { layout, components, border, palette } = useTheme()

    const handlePopoverOpen = ( event, data ) => {
        setUserInfoPopoveAnchorEl( event.currentTarget )
        setHoveredUserData( data )
    }

    const handlePopoverClose = () => {
        setUserInfoPopoveAnchorEl( null )
        setHoveredUserData( null )
    }

    const debouncedFilterTextSetter = debounce( 'admin-activity-filter-text', value => setFilterText( value ), 500 )

    const handleSearchFieldChange = event => {
        setSearchText( event.target.value )
        debouncedFilterTextSetter( event.target.value )
    }

    const open = Boolean( userInfoPopoveAnchorEl ) && Boolean( hoveredUserData )

    const removeFilters = () => {
        setFilterDate( null )
        setFilterText( "" )
    }

    const fetchLogs = useCallback( async ( append = false ) => {
        setLoading( true )
        try {
            const { data } = await api.logging.fetchAdminActivityLogs( { page, limit: logsPerPage } )
            setLogs( data.data )
            setError( null )
        } catch ( err ) {
            setError( { code: err?.response?.status, message: err?.response?.data?.message } )
        } finally {
            setLoading( false )
        }
    }, [page, logsPerPage] )

    const filteredLogs = useMemo( () => {

        let formattedFilter
        const searchFilter = filterText?.trim()?.toLowerCase()

        if ( filterDate )
            formattedFilter = dayjs( dayjs( filterDate ).format( "YYYY-MM-DD" ) )

        return logs.filter( log => {
            let include = true
            if ( searchFilter ) {
                const searchTestText = `${log.entity || log.resource || ""} ${log.auditor_details?.user_name || ""} ${log.auditor_details?.user_id || ""} ${log?.impersonated_user_details?.name || ""}`.toLowerCase()
                include = include && searchTestText?.toLowerCase()?.includes( searchFilter )
            }
            if ( formattedFilter ) {
                const diff = dayjs( log.createdAt ).diff( formattedFilter )
                include = include && 0 < diff && diff < oneDayMilliseconds
            }
            return include
        } )
    }, [logs, filterText, filterDate] )


    useEffect( () => {
        fetchLogs()
    }, [fetchLogs] )


    return (
        <Box flexGrow={1} padding={layout.pagePadding} overflow="auto" display="flex" flexDirection="column" gap={layout.sectionsGap} >
            <Box display="flex" gap={layout.cardsGap} justifyContent="space-between" alignItems="flex-end">
                <Box>
                    <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px">Admin Activity Logs</Typography>
                    <Typography variant="subtitle2" fontSize="12px" color='textSecondary'>Check main resource operations performed by admins.</Typography>
                </Box>
                <Button onClick={fetchLogs} variant='outlined' size="small" >Refresh Logs</Button>
            </Box>

            {/* FILTERS */}
            <Box gap={layout.cardsGap} display="flex">
                <ButtonGroup>
                    <Box display="flex" sx={{ cursor: "pointer", }} position="relative">
                        <Button size='small' variant='outlined' onClick={() => setDatePickerOpenState( p => true )} sx={{ cursor: "pointer", paddingX: "15px", color: palette.common.font, }} endIcon={Icons.default.KeyboardArrowDownIcon} startIcon={Icons.default.Calendar} color="greyed">
                            {formatDate( filterDate, { format: "DD-MM-YYYY" } ) || "Filter by date"}
                        </Button>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DateTimePicker
                                open={datePickerOpenState}
                                onClose={() => setDatePickerOpenState( false )}
                                {...( components?.MuiPickerOutlinedInput?.defaultProps || {} )}
                                id="startdatetime"
                                slotProps={{
                                    textField: {
                                        onClick: () => setDatePickerOpenState( true ),
                                        sx: {
                                            cursor: "pointer",
                                            opacity: 0,
                                            position: "absolute",
                                            inset: 0,
                                            pointerEvents: "none"
                                        }
                                    }
                                }}
                                slots={{
                                    openPickerButton: () => null,
                                }}
                                views={['year', 'month', 'day']}
                                onChange={val => {
                                    if ( val.isValid() )
                                        setFilterDate( val )
                                }}
                            />
                        </LocalizationProvider>
                    </Box>

                    {filterDate && <Tooltip onClick={() => setFilterDate( null )} title="Remove date filter" >
                        <Button color='greyed'>
                            {Icons.default.CancelIcon}
                        </Button>
                    </Tooltip>}
                </ButtonGroup>

                <CustomSearchBar style={{ flexGrow: 1 }} size='small' placeholder='Search by Resource name or Admin name or User ID...' value={searchText} onChange={handleSearchFieldChange} />
            </Box>


            {/* User Details Popover */}
            <Popover
                id="mouse-over-popover"
                sx={{ pointerEvents: 'none' }}
                open={open}
                anchorEl={userInfoPopoveAnchorEl}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                onClose={handlePopoverClose}
                slotProps={{
                    paper: {
                        elevation: 0,
                        sx: {
                            boxShadow: "none"
                        }
                    },
                    transition: {
                        timeout: { exit: 0 }
                    }
                }}
                disableRestoreFocus
            >
                <Box width={300} bgcolor={palette.contentBg}>
                    <Box display="flex" justifyContent="space-between" gap={layout.cardsGap} alignItems="flex-start" padding="10px">
                        <Box>
                            <Typography variant='h6' fontWeight="800" fontSize="16px" component="h1">
                                {hoveredUserData?.name}
                            </Typography>
                            {hoveredUserData?.role !== "STUDENT" && <Typography fontSize="12px" color='textSecondary'>
                                {hoveredUserData?.empcode}
                            </Typography>}
                            {hoveredUserData?.role === "STUDENT" && <Typography display="flex" alignItems="center" gap="5px" fontSize="12px" color='textSecondary'>
                                <Typography component="span" fontSize="inherit" fontWeight="inherit">{hoveredUserData?.user_id}</Typography>
                                {hoveredUserData?.usn && <Typography display="flex" alignItems="center" component="span" fontSize="5px" fontWeight="inherit">{Icons.default.FiberManualRecordIcon}</Typography>}
                                {hoveredUserData?.usn && <Typography component="span" fontSize="inherit" fontWeight="inherit">{hoveredUserData?.usn}</Typography>}
                            </Typography>}
                        </Box>
                        <CustomChip variant='filled' color="primary" value={hoveredUserData?.role} />
                    </Box>
                    <Divider />
                    {hoveredUserData?.role === "STUDENT" && <Box padding="10px">
                        <Box display="flex" justifyContent="space-between" gap={layout.cardsGap} alignItems="center" >
                            <Typography color='textSecondary' fontSize="12px" variant='subtitle2'>Institute</Typography>
                            <Typography fontSize="12px" textAlign="right" variant='subtitle2'>{hoveredUserData?.institute || "-"}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" gap={layout.cardsGap} alignItems="center" >
                            <Typography color='textSecondary' fontSize="12px" variant='subtitle2'>Course</Typography>
                            <Typography fontSize="12px" textAlign="right" variant='subtitle2'>{hoveredUserData?.course_short_name || "-"}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" gap={layout.cardsGap} alignItems="center" >
                            <Typography color='textSecondary' fontSize="12px" variant='subtitle2'>Branch</Typography>
                            <Typography fontSize="12px" textAlign="right" variant='subtitle2'>{hoveredUserData?.course_branch_short_name || "-"}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" gap={layout.cardsGap} alignItems="center" >
                            <Typography color='textSecondary' fontSize="12px" variant='subtitle2'>Current Year</Typography>
                            <Typography fontSize="12px" textAlign="right" variant='subtitle2'>{hoveredUserData?.current_year || "-"}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" gap={layout.cardsGap} alignItems="center" >
                            <Typography color='textSecondary' fontSize="12px" variant='subtitle2'>Current Semester</Typography>
                            <Typography fontSize="12px" textAlign="right" variant='subtitle2'>{hoveredUserData?.current_sem || "-"}</Typography>
                        </Box>
                    </Box>}
                    {hoveredUserData?.role !== "STUDENT" && <Box padding="10px">
                        <Box display="flex" justifyContent="space-between" gap={layout.cardsGap} alignItems="center" >
                            <Typography color='textSecondary' fontSize="12px" variant='subtitle2'>Institute</Typography>
                            <Typography fontSize="12px" textAlign="right" variant='subtitle2'>{hoveredUserData?.institute || "-"}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" gap={layout.cardsGap} alignItems="center" >
                            <Typography color='textSecondary' fontSize="12px" variant='subtitle2'>Department</Typography>
                            <Typography fontSize="12px" textAlign="right" variant='subtitle2'>{hoveredUserData?.department_name || "-"}</Typography>
                        </Box>
                    </Box>}
                </Box>
            </Popover>

            {!loading && !error && filteredLogs.length > 0 && <Box display="flex" flexGrow={1} overflow="auto" gap={layout.cardsGap} flexDirection="column">
                {filteredLogs.map( log => <LogBox handlePopoverOpen={handlePopoverOpen} handlePopoverClose={handlePopoverClose} log={log} id={log._id} /> )}
            </Box>}
            {!loading && !error && filteredLogs.length === 0 && < Box alignItems="center" display="flex" gap="10px" flexDirection="column" borderRadius={layout.borderRadius} bgcolor={palette.contentBg} padding="40px 20px"  >
                <Box component="img" width="180px" src={noLogsImg} />
                <Typography variant="subtitle2">No logs to show{( filterDate || filterText ) ? <em> for the applied filters</em> : ""}!</Typography>
                {( filterDate || filterText ) && <Button size='small' onClick={removeFilters} variant="outlined" color="greyed" sx={{ textTransform: "capitalize" }} startIcon={Icons.default.CloseIcon}>  <em> Remove Filters</em> </Button>}
            </Box>}

            {loading && !error && <DataLoadingSpinner waitingMessage='Loading activity logs! please wait...' padding="0" />}
            {error && <TryAgainMessageBlock getData={fetchLogs} err={error?.message} code={error?.code} loading={loading} />}
        </Box>
    )
}

export default AdminActivityLogs
